// Apolo IDE — TodosPanel (Sprint v6 B2).
//
// Live view of the session's TodoWrite list. Status chips: pending,
// in_progress (only one expected at a time per Claude Code convention),
// completed. Read-only — agent is the writer.

import { useCallback, useEffect, useState } from 'react';
import { CheckCircle2, Circle, Loader2, RefreshCcw } from 'lucide-react';
import { apiFetch } from '../../../lib/api.js';

const STATUS_ICON = {
  pending: Circle,
  in_progress: Loader2,
  completed: CheckCircle2,
};

const STATUS_CLASS = {
  pending: 'text-apolo-textTertiary',
  in_progress: 'text-apolo-info animate-spin',
  completed: 'text-apolo-success',
};

export default function TodosPanel({ sessionId }) {
  const [todos, setTodos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    if (!sessionId) return;
    setLoading(true);
    const res = await apiFetch(`/api/code-ide/sessions/${sessionId}/todos`);
    setLoading(false);
    if (!res.ok) { setError(res.error || 'Falha ao carregar todos'); return; }
    setError(null);
    setTodos(Array.isArray(res.data?.todos) ? res.data.todos : []);
  }, [sessionId]);

  useEffect(() => { load(); }, [load]);

  // Poll every 5s while panel is open so live updates from the agent appear.
  useEffect(() => {
    if (!sessionId) return undefined;
    const t = setInterval(load, 5000);
    return () => clearInterval(t);
  }, [sessionId, load]);

  if (loading && todos.length === 0) {
    return (
      <div role="status" aria-live="polite" className="px-4 py-3 text-xs text-apolo-textTertiary">
        Carregando todos…
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-2 px-3 py-3">
      {error && (
        <div role="alert" className="rounded-apolo-md border border-apolo-error/40 bg-apolo-errorBg p-2 text-xs text-apolo-error">
          {error}
        </div>
      )}

      <div className="flex items-center justify-between text-xs text-apolo-textTertiary uppercase tracking-widest">
        <span>Todos da sessão</span>
        <button type="button" onClick={load} aria-label="Atualizar todos" className="hover:text-apolo-textSecondary">
          <RefreshCcw size={12} />
        </button>
      </div>

      {todos.length === 0 ? (
        <div className="rounded-apolo-md border border-apolo-borderSubtle bg-apolo-surface2 p-4 text-center text-xs text-apolo-textTertiary">
          Nenhum todo. O agente cria à medida que executa.
        </div>
      ) : (
        <ol className="flex flex-col gap-1">
          {todos.map((t) => {
            const Icon = STATUS_ICON[t.status] || Circle;
            const text = t.status === 'in_progress' ? (t.active_form || t.content) : t.content;
            return (
              <li
                key={t.id || `${t.idx}-${t.content}`}
                className="flex items-start gap-2 rounded-apolo-md border border-apolo-borderSubtle bg-apolo-surface2 px-3 py-2"
              >
                <Icon size={14} className={`mt-0.5 shrink-0 ${STATUS_CLASS[t.status] || ''}`} aria-hidden="true" />
                <span className={`text-sm ${t.status === 'completed' ? 'text-apolo-textTertiary line-through' : 'text-apolo-textPrimary'}`}>{text}</span>
              </li>
            );
          })}
        </ol>
      )}
    </div>
  );
}
